/**
 * Personalakte-Tab „Beauftragungen" (G-9): Liste mit Status-Badge
 * (gültig / abgelaufen / unvollständig) + CRUD-Dialog (Typ, Zeitraum).
 * Schreiben: personal_schreiben; löschen: loeschen.
 */

import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { EntityFormSheet, type FieldDef, type FormValues } from "@/components/admin/EntityFormSheet"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { fmtDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { Assignment, AssignmentState } from "@/types"

const ASSIGNMENT_STATE: Record<AssignmentState, { label: string; badge: string }> = {
  valid: { label: "Gültig", badge: "border-green-300 bg-green-100 text-green-800" },
  expired: { label: "Abgelaufen", badge: "border-red-300 bg-red-100 text-red-800" },
  incomplete: {
    label: "Unvollständig",
    badge: "border-yellow-300 bg-yellow-100 text-yellow-800",
  },
}

export function AssignmentsTab({
  employeeId,
  canWrite,
}: {
  employeeId: string
  canWrite: boolean
}) {
  const queryClient = useQueryClient()
  const canDelete = useCan("loeschen")
  const [dialog, setDialog] = useState<{ open: boolean; row: Assignment | null }>({
    open: false,
    row: null,
  })

  const types = useQuery({
    queryKey: ["assignment-types"],
    queryFn: () => api.organization.assignmentTypes.list({ size: 100 }),
  })

  const query = useQuery({
    queryKey: ["assignments", "employee", employeeId],
    queryFn: () => api.assignments.list({ employee: Number(employeeId), size: 100 }),
  })

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["assignments"] })
    // Beauftragungs-Ampel im Kopf + Verzeichnis aktualisieren (G-9)
    queryClient.invalidateQueries({ queryKey: ["employee", employeeId] })
    queryClient.invalidateQueries({ queryKey: ["employees"] })
  }

  const save = useMutation({
    mutationFn: ({ id, values }: { id: string | null; values: FormValues }) => {
      const base = {
        assignmentTypeId: Number(values.assignmentTypeId),
        startDate: String(values.startDate) || null,
        endDate: String(values.endDate) || null,
      }
      return id
        ? api.assignments.update(id, base)
        : api.assignments.create({ ...base, employeeId: Number(employeeId) })
    },
    onSuccess: (_data, vars) => {
      invalidate()
      toast.success(vars.id ? "Beauftragung aktualisiert" : "Beauftragung angelegt")
      setDialog({ open: false, row: null })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.assignments.remove(id),
    onSuccess: () => {
      invalidate()
      toast.success("Beauftragung gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const rows = query.data?.data ?? []
  const editing = dialog.row

  const fields: FieldDef[] = [
    {
      key: "assignmentTypeId",
      label: "Beauftragungstyp",
      kind: "select",
      required: true,
      emptyOption: "— wählen —",
      options: (types.data?.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    },
    { key: "startDate", label: "Beginn", kind: "date" },
    { key: "endDate", label: "Ende", kind: "date" },
  ]

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<Assignment>[]>(() => {
    const defs: ColumnDef<Assignment>[] = [
      {
        id: "typeName",
        header: "Beauftragung",
        accessorFn: (a) => a.typeName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "period",
        header: "Zeitraum",
        // ISO-Startdatum sortiert chronologisch korrekt
        accessorFn: (a) => a.startDate ?? "",
        cell: ({ row }) => {
          const a = row.original
          return a.startDate || a.endDate ? `${fmtDate(a.startDate)} – ${fmtDate(a.endDate)}` : "—"
        },
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (a) => ASSIGNMENT_STATE[a.status].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={ASSIGNMENT_STATE[row.original.status].badge}>
            {ASSIGNMENT_STATE[row.original.status].label}
          </Badge>
        ),
      },
    ]
    if (canWrite || canDelete) {
      defs.push({
        id: "aktionen",
        header: "Aktionen",
        enableSorting: false,
        enableHiding: false,
        meta: { headClassName: "w-24 text-right", cellClassName: "text-right" },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            {canWrite && (
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="Bearbeiten"
                onClick={() => setDialog({ open: true, row: row.original })}
              >
                <Pencil className="!size-3.5" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-destructive hover:text-destructive"
                aria-label="Löschen"
                disabled={del.isPending}
                onClick={() => {
                  if (window.confirm("Beauftragung wirklich löschen?")) del.mutate(row.original.id)
                }}
              >
                <Trash2 className="!size-3.5" />
              </Button>
            )}
          </div>
        ),
      })
    }
    return defs
  }, [canWrite, canDelete, del])

  return (
    <div className="space-y-3">
      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={() => setDialog({ open: true, row: null })}>
            <Plus /> Neue Beauftragung
          </Button>
        </div>
      )}
      <Card>
        <CardContent className="py-2">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="employee-assignments"
              emptyMessage="Keine Beauftragungen erfasst."
            />
          )}
        </CardContent>
      </Card>

      <EntityFormSheet
        sheetKey="entity-form-assignments"
        open={dialog.open}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
        title={editing ? "Beauftragung bearbeiten" : "Neue Beauftragung"}
        fields={fields}
        initialValues={{
          assignmentTypeId: editing?.assignmentTypeId ?? "",
          startDate: editing?.startDate ?? "",
          endDate: editing?.endDate ?? "",
        }}
        pending={save.isPending}
        onSubmit={(values) => save.mutate({ id: editing?.id ?? null, values })}
      />
    </div>
  )
}
